import React, { useState, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  Chip,
  Button,
  Tooltip
} from '@mui/material';
import Autocomplete from '@mui/lab/Autocomplete';
import useStyles from './useStyles';
import { withScoutTheme } from '../../../ThemeContext/ThemeContext';
import PropTypes from 'prop-types';

import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

function SelectRecipients({
  statuses,
  applications,
  setSelectedApplications,
  setIsSelectModalOpen
}) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [unselectedStatuses, setUnselectedStatuses] = useState([]);

  const classes = useStyles();

  useEffect(() => {
    setUnselectedStatuses(
      statuses
        .filter((status) => applications.map((app) => app.status).includes(status.id))
        .filter((status) => !selectedStatuses.map((selected) => selected.id).includes(status.id))
    );
  }, [selectedStatuses]);

  useEffect(() => {
    setSelectedStatuses(
      selectedStatuses.filter((status) =>
        selectedUsers.map((app) => app.status).includes(status.id)
      )
    );
  }, [selectedUsers]);

  function handleStatusRemove(id) {
    setSelectedStatuses(selectedStatuses.filter((selected) => selected.id !== id));
    setSelectedUsers(selectedUsers.filter((app) => app.status !== id));
  }

  function handleStatusAdd(e) {
    const newApplicants = applications
      .filter((app) => app.status === e.target.value.id)
      .filter((app) => !selectedUsers.map((selected) => selected.id).includes(app.id));
    setSelectedStatuses([...selectedStatuses, e.target.value]);
    setSelectedUsers([...selectedUsers, ...newApplicants]);
  }

  function handleClick() {
    setSelectedApplications(selectedUsers);
    setIsSelectModalOpen(false);
  }

  return (
    <div className={classes.root}>
      <h4>Select Recipients</h4>
      <div className={classes.searchContainer}>
        <Autocomplete
          classes={{
            root: classes.autocompleteField,
            inputRoot: classes.autoCompleteInput,
            endAdornment: classes.endAdornment
          }}
          multiple
          disableClearable
          options={applications}
          getOptionLabel={(option) => option.name}
          value={selectedUsers}
          filterSelectedOptions={true}
          onChange={(e, newValue) => {
            setSelectedUsers(newValue);
          }}
          variant="outlined"
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              classes={{ root: classes.search }}
              placeholder="Search Users"
              InputLabelProps={{
                classes: { root: classes.search }
              }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Select
          className={classes.inputBase}
          classes={{ root: classes.select }}
          displayEmpty={true}
          renderValue={() => 'Filter candidates'}
          variant="outlined"
          value=""
          onChange={handleStatusAdd}
        >
          {unselectedStatuses.map((status) => (
            <MenuItem key={status.id} value={status}>
              {status.name}
            </MenuItem>
          ))}
        </Select>
      </div>
      {selectedStatuses.length > 0 && (
        <div className={classes.chipContainer}>
          {selectedStatuses.map((status) => (
            <Chip
              key={status.id}
              className={classes.chipItem}
              label={status.name}
              value={status}
              onDelete={() => handleStatusRemove(status.id)}
              variant="outlined"
            />
          ))}
        </div>
      )}
      <div className={classes.table}>
        <div className={classes.row}>
          <div className={`${classes.tableHeader} ${classes.tableLg}`}>Name</div>
          <div className={`${classes.tableHeader} ${classes.tableMd}`}>Phone</div>
          <div className={`${classes.tableHeader} ${classes.tableLg}`}>Status</div>
          <div></div>
        </div>
        {selectedUsers.length <= 0 ? (
          <div className={classes.row}>
            <span className={classes.noApplicants}>No applicants selected</span>
          </div>
        ) : (
          selectedUsers.map((app) => (
            <div key={app.id} className={classes.row}>
              <div className={classes.tableLg}>{app.name}</div>
              <div className={classes.tableMd}>{app.phone}</div>
              <div className={classes.tableLg}>
                {statuses.find((status) => status.id === app.status)
                  ? statuses.find((status) => status.id === app.status).name
                  : 'None'}
              </div>
              <IconButton
                onClick={() =>
                  setSelectedUsers(selectedUsers.filter((selected) => selected.id !== app.id))
                }
              >
                <HighlightOffIcon color="error" />
              </IconButton>
            </div>
          ))
        )}
      </div>
      <div className={classes.buttonContainer}>
        {selectedUsers.length <= 1 ? (
          <Tooltip title="Please select 2 or more applicants">
            <span>
              <Button
                disabled={true}
                disableElevation
                variant="contained"
                color="secondary"
                onClick={handleClick}
                endIcon={<ChevronRightRoundedIcon />}
              >
                Next
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            disableElevation
            variant="contained"
            color="secondary"
            onClick={handleClick}
            endIcon={<ChevronRightRoundedIcon />}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
}

SelectRecipients.propTypes = {
  statuses: PropTypes.array.isRequired,
  applications: PropTypes.array.isRequired,
  setSelectedApplications: PropTypes.func.isRequired,
  setIsSelectModalOpen: PropTypes.func.isRequired
};

export default withScoutTheme(SelectRecipients);
